import { Component, createSignal, onMount, Show, For } from "solid-js";
import {
  configureOAuth,
  createAuthorizationUrl,
  finalizeAuthorization,
  getSession,
  OAuthUserAgent,
  resolveFromIdentity,
  type Session,
} from "@atcute/oauth-browser-client";
import { XRPC } from "@atcute/client";

import { captureError } from "./sentry";

// Configure OAuth
configureOAuth({
  metadata: {
    client_id: import.meta.env.VITE_OAUTH_CLIENT_ID,
    redirect_uri: import.meta.env.VITE_OAUTH_REDIRECT_URL,
  },
});

// Fallback servers
const FALLBACK_SERVERS = [
  "https://public.api.bsky.app",
  "https://api.bsky.app",
];

// Utility function to try multiple servers
const tryServers = async (requestFn: (rpc: XRPC) => Promise<any>, handler: OAuthUserAgent) => {
  for (const server of FALLBACK_SERVERS) {
    try {
      const rpc = new XRPC({ baseUrl: server, handler }); // Pass the handler here
      const response = await requestFn(rpc);
      return response;
    } catch (error) {
      console.error(`Request failed on server ${server}:`, error);
      // If the last server fails, throw the error
      if (server === FALLBACK_SERVERS[FALLBACK_SERVERS.length - 1]) {
        throw error;
      }
    }
  }
};

const App: Component = () => {
  const [loginState, setLoginState] = createSignal(false);
  const [handle, setHandle] = createSignal("");
  const [profile, setProfile] = createSignal<any>(null);
  const [notice, setNotice] = createSignal("");
  const [loginInput, setLoginInput] = createSignal("");
  const [starterPackLink, setStarterPackLink] = createSignal("");
  const [starterPackData, setStarterPackData] = createSignal<any>(null);
  const [listData, setListData] = createSignal<any>(null);
  const [sortFollowedFirst, setSortFollowedFirst] = createSignal(false);

  let agent: OAuthUserAgent;
  let rpc: XRPC;

const fetchProfile = async (handle: string) => {
  try {
    const profileResponse = await tryServers(async (rpc) => {
      return await rpc.get("app.bsky.actor.getProfile", {
        params: { actor: handle },
      });
    }, agent);
    setProfile(profileResponse.data);
  } catch (error) {
    if (error.response?.status === 401) {
      setNotice("Session expired. Please log in again.");
      logoutBsky();
    } else {
      console.error("Error fetching profile:", error);
      setNotice("Failed to fetch profile data.");
      captureError(error, { 
        handle,
        action: 'fetchProfile'
      });
    }
  }
};


  onMount(async () => {
    setNotice("Loading...");

    const init = async (): Promise<Session | undefined> => {
      const params = new URLSearchParams(location.hash.slice(1));

      if (params.has("state") && (params.has("code") || params.has("error"))) {
        history.replaceState(null, "", location.pathname + location.search);

        const session = await finalizeAuthorization(params);
        const did = session.info.sub;

        localStorage.setItem("lastSignedIn", did);
        return session;
      } else {
        const lastSignedIn = localStorage.getItem("lastSignedIn");

        if (lastSignedIn) {
          try {
            return await getSession(lastSignedIn);
          } catch (err) {
            localStorage.removeItem("lastSignedIn");
            throw err;
          }
        }
      }
    };

    const session = await init().catch(() => {});

    if (session) {
      agent = new OAuthUserAgent(session);
      rpc = new XRPC({ handler: agent });
      setLoginState(true);
      const userHandle = session.info.preferred_username || session.info.sub;
      setHandle(userHandle);
      await fetchProfile(userHandle); // Fetch profile data after login
    }

    setNotice("");
  });

  const loginBsky = async (handle: string) => {
    try {
      setNotice("Resolving your identity...");
      const resolved = await resolveFromIdentity(handle);

      setNotice("Contacting your data server...");
      const authUrl = await createAuthorizationUrl({
        scope: import.meta.env.VITE_OAUTH_SCOPE,
        ...resolved,
      });

      setNotice("Redirecting...");
      await new Promise((resolve) => setTimeout(resolve, 250));

      location.assign(authUrl);
    } catch {
      setNotice("Error during OAuth login");
    }
  };

  const logoutBsky = async () => {
    await agent.signOut();
    setLoginState(false);
  };

  async function resolveShortUrl(url: string) {
    try {
      const proxyUrl = `https://bsky.nabilrise.workers.dev/?url=${encodeURIComponent(url)}`;
      const response = await fetch(proxyUrl);
      const data = await response.json();

      const finalUrl = data.location;
      if (!finalUrl) {
        throw new Error('Failed to resolve short URL: No location found');
      }

      return finalUrl;
    } catch (error) {
      console.error('Error resolving short URL:', error);
      throw new Error('Failed to resolve short URL');
    }
  }

  const fetchStarterPack = async () => {
    const link = starterPackLink();
    if (!link) {
      setNotice("Please enter a valid Starter Pack link.");
      return;
    }
    let finalLink = link;
    if (link.includes('go.bsky.app')) {
      try {
        finalLink = await resolveShortUrl(link);
      } catch (error) {
        setNotice("Failed to resolve short URL.");
        captureError(error, {
          originalLink: link,
          action: 'resolveShortUrl'
         });
        return;
      }
    }

    try {
      const url = new URL(finalLink);
      const pathParts = url.pathname.split("/");
      const handle = pathParts[2];
      const rkey = pathParts[3];

      if (!handle || !rkey) {
        setNotice("Invalid Starter Pack link.");
        return;
      }

      const uri = `at://${handle}/app.bsky.graph.starterPack/${rkey}`;

      setNotice("Fetching Starter Pack data...");
      const starterPackResponse = await tryServers(async (rpc) => {
        return await rpc.get("app.bsky.graph.getStarterPack", {
          params: { starterPack: uri },
        });
      }, agent); // Pass the agent here

      setStarterPackData(starterPackResponse.data);
      setNotice("Starter Pack data fetched successfully!");

      const listUri = starterPackResponse.data.starterPack.record.list;
      if (!listUri) {
        setNotice("No list found in the Starter Pack.");
        return;
      }

      setNotice("Fetching list data...");
      let cursor: string | undefined;
      let allItems: any[] = [];

      do {
        const listResponse = await tryServers(async (rpc) => {
          return await rpc.get("app.bsky.graph.getList", {
            params: { list: listUri, limit: 100, cursor },
          });
        }, agent); // Pass the agent here

        allItems = allItems.concat(listResponse.data.items);
        cursor = listResponse.data.cursor;
      } while (cursor);

      const sortedItems = sortFollowedFirst()
        ? allItems.sort((a, b) => {
            if (a.subject.viewer.following && !b.subject.viewer.following) return -1;
            if (!a.subject.viewer.following && b.subject.viewer.following) return 1;
            return 0;
          })
        : allItems;

      setListData({ list: { ...starterPackResponse.data.starterPack.record, items: sortedItems } });
      setNotice("List data fetched successfully!");
    } catch (error) {
      console.error("Error fetching data:", error);
      setNotice("Failed to fetch data.");
      captureError(error, {
        link: finalLink,
        action: 'fetchStarterPack'
      });
    }
  };

  const unfollowAll = async () => {
    const items = listData().list.items;
    const followWrites = items
      .filter((item: any) => item.subject.viewer.following)
      .map((item: any) => ({
        $type: "com.atproto.repo.applyWrites#delete",
        collection: "app.bsky.graph.follow",
        rkey: item.subject.viewer.following.split("/").pop(),
      }));

    if (followWrites.length === 0) {
      setNotice("No accounts to unfollow.");
      return;
    }

    try {
      setNotice("Unfollowing all accounts...");
      await tryServers(async (rpc) => {
        await rpc.call("com.atproto.repo.applyWrites", {
          data: {
            repo: agent.sub,
            writes: followWrites,
          },
        });
      }, agent); // Pass the agent here
      setNotice(`Unfollowed ${followWrites.length} accounts.`);

      await new Promise((resolve) => setTimeout(resolve, 1000));
      await fetchStarterPack();
    } catch (error) {
      console.error("Error unfollowing accounts:", error);
      setNotice("Failed to unfollow accounts. Please try again later.");
    }
  };

  const unfollowUser = async (followUri: string) => {
    try {
      setNotice("Unfollowing account...");
      await tryServers(async (rpc) => {
        await rpc.call("com.atproto.repo.applyWrites", {
          data: {
            repo: agent.sub,
            writes: [
              {
                $type: "com.atproto.repo.applyWrites#delete",
                collection: "app.bsky.graph.follow",
                rkey: followUri.split("/").pop(),
              },
            ],
          },
        });
      }, agent); // Pass the agent here
      setNotice("Account unfollowed successfully.");

      await new Promise((resolve) => setTimeout(resolve, 1000));
      await fetchStarterPack();
    } catch (error) {
      console.error("Error unfollowing account:", error);
      setNotice("Failed to unfollow account.");
      captureError(error, {
        accountCount: followWrites.length,
        action: 'unfollowAll'
      });
    }
  };

  const toggleSortFollowedFirst = () => {
    setSortFollowedFirst(!sortFollowedFirst());
    if (listData()) {
      const sortedItems = sortFollowedFirst()
        ? listData().list.items.sort((a: any, b: any) => {
            if (a.subject.viewer.following && !b.subject.viewer.following) return -1;
            if (!a.subject.viewer.following && b.subject.viewer.following) return 1;
            return 0;
          })
        : listData().list.items;
      setListData({ list: { ...listData().list, items: sortedItems } });
    }
  };

  const refreshList = async () => {
    setNotice("Refreshing list...");
    await fetchStarterPack();
    setNotice("List refreshed successfully.");
  };

  return (
    <div class="min-h-screen bg-gray-50 flex flex-col">
      <div class="flex-grow">
        <nav class="bg-white shadow-sm">
          <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div class="flex justify-between items-center">
              <h1 class="text-xl font-bold text-gray-800">UnfollowPack</h1>
              <Show when={loginState() && handle()}>
                <div class="flex items-center space-x-4">
                  <Show when={profile()}>
                    <span class="text-sm text-gray-600">
                      {profile().displayName} @{profile().handle}
                    </span>
                  </Show>
                  <button
                    onclick={() => logoutBsky()}
                    class="text-sm text-red-600 hover:text-red-800 font-medium"
                  >
                    Logout
                  </button>
                </div>
              </Show>
            </div>
          </div>
        </nav>

        <main class="max-w-4xl mx-auto px-4 py-8">
          <Show when={!loginState() && !notice().includes("Loading")}>
            <div class="bg-white rounded-lg shadow-sm p-8 max-w-md mx-auto">
              <h2 class="text-2xl font-bold text-gray-800 mb-2 text-center">UnfollowPack</h2>
              <p class="text-gray-600 text-center mb-6">Helps you unfollow a starter pack in one click</p>
              <form class="space-y-4" onsubmit={(e) => e.preventDefault()}>
                <div>
                  <label for="handle" class="block text-sm font-medium text-gray-700 mb-1">
                    Bluesky Handle (without @)
                  </label>
                  <input
                    type="text"
                    id="handle"
                    placeholder="user.bsky.social"
                    class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onInput={(e) => setLoginInput(e.currentTarget.value.toLowerCase())}
                  />
                </div>
                <button
                  onclick={() => loginBsky(loginInput())}
                  class="w-full bg-blue-600 text-white rounded-lg py-2 font-medium hover:bg-blue-700 transition duration-150"
                >
                  Sign In
                </button>
              </form>
            </div>
          </Show>

          <Show when={loginState() && handle()}>
            <div class="bg-white rounded-lg shadow-sm p-6">
              <div class="space-y-4">
                <label for="starterPackLink" class="block text-sm font-medium text-gray-700">
                  Enter Starter Pack Link (or short-url, ex: https://go.bsky.app/E2q6ZKa)
                </label>
                <input
                  type="text"
                  id="starterPackLink"
                  placeholder="https://bsky.app/starter-pack/username/id"
                  class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onInput={(e) => setStarterPackLink(e.currentTarget.value)}
                />
                <button
                  onclick={() => fetchStarterPack()}
                  class="w-full bg-blue-600 text-white rounded-lg py-2 font-medium hover:bg-blue-700 transition duration-150"
                >
                  Load Starter Pack
                </button>
              </div>
            </div>
          </Show>

          <Show when={notice()}>
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 my-8">
              <p class="text-blue-800 text-sm text-center">{notice()}</p>
            </div>
          </Show>

          <Show when={starterPackData()}>
            <div class="bg-white rounded-lg shadow-sm p-6 mb-8">
              <div class="space-y-4">
                <div class="flex items-center justify-between">
                  <h2 class="text-xl font-bold text-gray-800">
                    {starterPackData().starterPack.record.name}
                  </h2>
                  <span class="text-sm text-gray-600">
                    by @{starterPackData().starterPack.creator.handle}
                  </span>
                </div>
                <p class="text-gray-600">{starterPackData().starterPack.record.description}</p>
                <div class="flex space-x-6">
                  <div class="text-sm">
                    <span class="text-gray-500">All Time Joins:</span>
                    <span class="ml-2 font-medium text-gray-900">
                      {starterPackData().starterPack.joinedAllTimeCount}
                    </span>
                  </div>
                  <div class="text-sm">
                    <span class="text-gray-500">This Week:</span>
                    <span class="ml-2 font-medium text-gray-900">
                      {starterPackData().starterPack.joinedWeekCount}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Show>

          <Show when={listData()}>
            <div class="bg-white rounded-lg shadow-sm p-6">
              <div class="mb-6">
                <div class="flex items-center justify-between mb-4">
                  <h2 class="text-xl font-bold text-gray-800">List Members</h2>
                  <div class="text-sm">
                    <span class="text-gray-500">Following:</span>
                    <span class="ml-2 font-medium text-gray-900">
                      {listData().list.items.filter((item: any) => item.subject.viewer.following).length}
                      /{listData().list.items.length}
                    </span>
                  </div>
                </div>
                <div class="flex flex-wrap gap-2">
                  <button
                    onclick={() => unfollowAll()}
                    class="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition duration-150"
                  >
                    Unfollow All
                  </button>
                  <button
                    onclick={() => toggleSortFollowedFirst()}
                    class="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition duration-150"
                  >
                    {sortFollowedFirst() ? "Default Sort" : "Show Following First"}
                  </button>
                  <button
                    onclick={() => refreshList()}
                    class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition duration-150"
                  >
                    Refresh List
                  </button>
                </div>
              </div>

              <div class="space-y-4">
                <For each={listData().list.items}>
                  {(item: any) => (
                    <div class="flex items-start space-x-4 p-4 rounded-lg hover:bg-gray-50 transition duration-150">
                      <div class="flex-shrink-0">
                        <div class="w-12 h-12 rounded-full overflow-hidden bg-gray-100">
                          <img
                            src={item.subject.avatar || "https://cdn.bsky.app/img/avatar/plain/did:plc:default/bafkreicjjja5miscnstcwtngcymc6xxtxqdxzgtpcq4tj6do4l2n7keugq@jpeg"}
                            alt="Avatar"
                            class="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                      <div class="flex-grow">
                        <div class="flex items-center justify-between">
                          <div>
                            <p class="font-medium text-gray-900">{item.subject.displayName}</p>
                            <p class="text-sm text-gray-500">@{item.subject.handle}</p>
                          </div>
                          <Show when={item.subject.viewer.following}>
                            <button
                              onclick={() => unfollowUser(item.subject.viewer.following)}
                              class="px-3 py-1 text-sm text-red-600 font-medium rounded-md hover:bg-red-50 transition duration-150"
                            >
                              Unfollow
                            </button>
                          </Show>
                        </div>
                        <Show when={item.subject.description}>
                          <p class="mt-2 text-sm text-gray-600 line-clamp-2">
                            {item.subject.description}
                          </p>
                        </Show>
                      </div>
                    </div>
                  )}
                </For>
              </div>
            </div>
          </Show>
        </main>
      </div>

      <footer class="bg-white mt-auto py-4 shadow-sm">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p class="text-center text-gray-600">
            Made by{" "}
            <a
              href="https://bsky.app/profile/did:plc:enmts4dypssoblpsjop4zxyc"
              target="_blank"
              rel="noopener noreferrer"
              class="text-blue-600 hover:text-blue-800"
            >
              Nabil
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default App;
